import { useStyletron } from "baseui";
import { BacnetObjectState, BacnetObjectTypes, BacnetPropertyIds, BacnetReliability, getReliabilityString, GetUnitsString, isBinary, isInteger, isMultiState } from "../../services/mapping/bacnet";
import { useGetObjectPropertiesQuery } from "../../services/mapping/bacnet/bacnetStateApi";
import { useMemo } from "react";
import { Block } from "baseui/block";
import { Checkbox } from "baseui/checkbox";
import { ListItem, ListItemLabel } from "baseui/list";
import { RiAlarmWarningLine } from "react-icons/ri";
import { VscReplace, VscWarning } from "react-icons/vsc";
import { MdSensorsOff } from "react-icons/md";
import { LabelLarge, LabelXSmall, ParagraphXSmall } from "baseui/typography";
import { BacnetObjectIcon } from "./BacnetObjectIcon";
import { BacnetCheckObjectMapping } from "./BacnetCheckMapping";
import { CreateConfigurationDialog } from "./";
import { Button } from "baseui/button";
import { ChevronRight } from "baseui/icon";
import { themedUseStyletron } from "../../theme/customTheme";
import { useIntl } from "react-intl";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import TimeAgo from 'react-timeago'
import { useTimeAgoFormatter } from "../../hooks/useTimeAgoFormatter";
import { HorizontalStackPanel, VerticalStackPanel } from "../../components/Layout/StackPanel";
import { IoCalendarOutline } from "react-icons/io5";
import { IAutomationObject } from "../../automationObjects";


const BacnetExplorerListItem = (props:
    {
        item: BacnetObjectState,
        edgeDeviceId: string,
        deviceId: number,
        moduleId: string,
        resourceGroupId: string,
        onToggleSelection: (item: any, isChecked: boolean) => void,
        onShowDetails: (item: any) => void
    }) => {
    const [css, theme] = useStyletron();
    const { item, onToggleSelection: toggle } = props;
    const { data, error, isLoading, isSuccess, currentData } = useGetObjectPropertiesQuery({ edgeDeviceId: props.edgeDeviceId, deviceId: props.deviceId, instanceId: item.instanceId, objectType: item.objectType });
    const statusFlags = useMemo(() => data?.find(v => v.propertyType === BacnetPropertyIds.STATUS_FLAGS)?.value.value ?? 0x00, [data]);
    const reliability = useMemo(() => data?.find(v => v.propertyType === BacnetPropertyIds.RELIABILITY)?.value.value ?? BacnetReliability.NoFaultDetected, [data]);
    const siemensPropDescr = useMemo(() => data?.find(v => v.propertyType === BacnetPropertyIds.SIEMENS_PROPRIETARY_DESCR)?.value.value ?? undefined, [data]);

    const { inAlarm, fault, overridden, outofservice } = getStatusFlags(statusFlags);

    return (
        <Block display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <Checkbox
                checked={item.selected}
                onChange={(args: any) => { toggle(item, args.currentTarget.checked) }}
            />
            <ListItem
                overrides={{
                    ArtworkContainer: {
                        style: ({ $theme }) => ({
                            color: $theme.colors.contentTertiary
                        })
                    }
                }}
                artwork={() => <BacnetObjectIcon type={item.objectType}></BacnetObjectIcon>}
                endEnhancer={() =>
                    <Block display={'flex'} flexDirection={'row'} alignItems={'center'} gridGap={'5px'}>

                        <ErrorBoundary>
                            <EndEnhancer item={item} onSelect={props.onShowDetails} edgeDeviceId={props.edgeDeviceId} deviceId={props.deviceId} moduleId={props.moduleId}></EndEnhancer>
                        </ErrorBoundary>
                        <BacnetCheckObjectMapping  edgeDeviceId={props.edgeDeviceId} deviceId={props.deviceId} instanceId={item.instanceId} objectType={item.objectType}></BacnetCheckObjectMapping>

                        <CreateConfigurationDialog  resourceGroupId={props.resourceGroupId} bacnetDeviceId={props.deviceId} bacnetObject={item}></CreateConfigurationDialog>
                        <Button onClick={() => { props.onShowDetails(item); }} size="default" kind="tertiary" shape="round">
                            <ChevronRight size='scale1000' />
                        </Button>
                    </Block>
                }>
                <ListItemLabel description={<Block gridGap={theme.sizing.scale600}>
                    {item.objectDescription}
                    <ParagraphXSmall marginTop={0} marginBottom={0}>{siemensPropDescr}</ParagraphXSmall>
                </Block>} >
                    <Block display={'flex'} flexWrap gridGap={theme.sizing.scale400} alignItems={'center'}>
                        <div>{item.objectName}</div>
                        <div>{BacnetObjectTypes[item.objectType]} [{item.instanceId}]</div>

                        {inAlarm && <RiAlarmWarningLine title='In Alarm State' size={theme.sizing.scale800} color={theme.colors.negative}></RiAlarmWarningLine>}
                        {fault && <VscWarning title='Fault State' size={theme.sizing.scale800} color={theme.colors.warning} />}
                        {overridden && <VscReplace title='Overriden on device' size={theme.sizing.scale800} color={theme.colors.accent}></VscReplace>}
                        {outofservice && <MdSensorsOff title='Out of Service' size={theme.sizing.scale800} color={theme.colors.warning}></MdSensorsOff>}

                        {reliability !== BacnetReliability.NoFaultDetected && <ParagraphXSmall margin={0} color={theme.colors.warning600}>{getReliabilityString(reliability)}</ParagraphXSmall>}
                    </Block>
                </ListItemLabel>
            </ListItem>
        </Block>
    )
}

export default BacnetExplorerListItem;



const EndEnhancer = ({ item, onSelect, edgeDeviceId, deviceId, moduleId, presentValue, unit }: any) => {
    const [css, theme] = themedUseStyletron();
    const intl = useIntl();
    const pv1 = fromatPresentValue(item, intl);
    const { formatter } = useTimeAgoFormatter();
    const units = item.unit ? GetUnitsString(Number(item.unit)) : "";
    const pv = isObject(pv1) ? JSON.stringify(pv1) : pv1;
    const color = isBinary(item.objectType) ? (Number(item.presentValue) > 0 ? theme.appColors.aggregate.on : theme.appColors.aggregate.off) : theme.colors.primary;

    return (
        <VerticalStackPanel $alignItems={'flex-end'} $gap={theme.sizing.scale100}>
            <HorizontalStackPanel><LabelLarge color={color}>{pv}</LabelLarge>
                <LabelXSmall>{units}</LabelXSmall>
            </HorizontalStackPanel>
            <ParagraphXSmall margin={0} color={theme.colors.contentTertiary}>
            <IoCalendarOutline></IoCalendarOutline> <TimeAgo date={item.presentValueTSource} formatter={formatter}></TimeAgo>
            </ParagraphXSmall>
        </VerticalStackPanel>
    );
}

function getStatusFlags(value: number) {
    const inAlarm = (value & 0x01) > 0;
    const fault = (value & 0x02) > 0;
    const overridden = (value & 0x04) > 0;
    const outofservice = (value & 0x08) > 0;
    return { inAlarm: inAlarm, fault: fault, overridden: overridden, outofservice: outofservice }
}

function isObject(val) {
    if (val === null) { return false; }
    return ((typeof val === 'function') || (typeof val === 'object'));
}

function fromatPresentValue(item: BacnetObjectState, intl: any) {

    if (item.presentValue === undefined)
        return "";
    if (item.presentValue === null)
        return "";

    if (isNaN(item.presentValue))
        return item.presentValue;
    if (isBinary(item.objectType)) {
        return Number(item.presentValue) > 0 ? intl.formatMessage({ id: "on", defaultMessage: "ON" }) : intl.formatMessage({ id: "off", defaultMessage: "OFF" });
    }
    if (isInteger(item.objectType) || isMultiState(item.objectType))
        return Number(item.presentValue).toFixed(0);

    return Number(item.presentValue).toFixed(2);
}